import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useQuery, useZero } from '@rocicorp/zero/react'
import { Shell } from '../components/Shell.tsx'
import { formatCents } from '../lib/format.ts'
import { mutators } from '../zero/mutators.ts'
import { queries } from '../zero/queries.ts'
import { Require } from './guards.tsx'

type MutationOutcome =
  | { readonly type: 'success' }
  | { readonly type: 'error'; readonly error: { readonly message: string } }
interface MutationHandle {
  client: Promise<MutationOutcome>
  server: Promise<MutationOutcome>
}

function CatalogueInner() {
  const z = useZero()
  const navigate = useNavigate()
  const [catalogue] = useQuery(queries.drinksCatalogue())
  const [description, setDescription] = useState('')
  const [price, setPrice] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingText, setEditingText] = useState('')
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)

  async function run(mutation: MutationHandle, pending: string, done: string): Promise<boolean> {
    const clientResult = await mutation.client
    if (clientResult.type === 'error') {
      setMessage(clientResult.error.message)
      return false
    }
    setMessage(pending)
    void mutation.server.then((serverResult) => {
      setMessage(serverResult.type === 'success' ? done : serverResult.error.message)
    })
    return true
  }

  async function addDrink(event: React.FormEvent) {
    event.preventDefault()
    const trimmed = description.trim()
    const priceNumber = Number(price)
    const priceCents = Math.round(priceNumber * 100)
    if (!trimmed) {
      setMessage('Enter a description')
      return
    }
    if (!Number.isFinite(priceNumber) || priceCents <= 0) {
      setMessage('Enter a price greater than zero')
      return
    }

    setBusy(true)
    setMessage('')
    try {
      const ok = await run(
        z.mutate(
          mutators.catalogue.create({
            drinkId: crypto.randomUUID(),
            description: trimmed,
            priceCents,
          }),
        ),
        'Drink added · syncing…',
        'Drink added',
      )
      if (ok) {
        setDescription('')
        setPrice('')
      }
    } catch {
      setMessage('Failed to add drink')
    } finally {
      setBusy(false)
    }
  }

  async function saveRename(drinkId: string) {
    const trimmed = editingText.trim()
    if (!trimmed) {
      setMessage('A description cannot be empty')
      return
    }
    setMessage('')
    try {
      const ok = await run(
        z.mutate(mutators.catalogue.rename({ drinkId, description: trimmed })),
        'Drink renamed · syncing…',
        'Drink renamed',
      )
      if (ok) setEditingId(null)
    } catch {
      setMessage('Failed to rename drink')
    }
  }

  async function removeDrink(drinkId: string, name: string) {
    if (!window.confirm(`Remove ${name}? Past purchases keep it at its original price.`)) return
    setMessage('')
    try {
      await run(
        z.mutate(mutators.catalogue.remove({ drinkId })),
        'Drink removed · syncing…',
        'Drink removed',
      )
    } catch {
      setMessage('Failed to remove drink')
    }
  }

  return (
    <Shell title="Drink catalogue" onBack={() => navigate({ to: '/admin' })}>
      <div className="section-title">Drinks ({catalogue.length})</div>
      <div className="card">
        {catalogue.length === 0 ? (
          <p className="empty">No drinks yet.</p>
        ) : (
          catalogue.map((drink) =>
            editingId === drink.drinkId ? (
              <div className="line" key={drink.drinkId}>
                <input
                  className="inline-edit"
                  autoFocus
                  maxLength={200}
                  value={editingText}
                  onChange={(event) => setEditingText(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') void saveRename(drink.drinkId)
                    if (event.key === 'Escape') setEditingId(null)
                  }}
                />
                <button className="btn-ghost" onClick={() => void saveRename(drink.drinkId)}>
                  Save
                </button>
                <button className="btn-ghost" onClick={() => setEditingId(null)}>
                  Cancel
                </button>
              </div>
            ) : (
              <div className="line" key={drink.drinkId}>
                <div className="line-main">
                  <div className="line-title">{drink.description}</div>
                  <div className="line-sub">Price fixed at creation</div>
                </div>
                <span className="line-amount">{formatCents(drink.priceCents)}</span>
                <button
                  className="btn-ghost"
                  onClick={() => {
                    setEditingId(drink.drinkId)
                    setEditingText(drink.description)
                  }}
                >
                  Rename
                </button>
                <button
                  className="btn-ghost"
                  onClick={() => void removeDrink(drink.drinkId, drink.description)}
                  title="Remove drink"
                >
                  ✕
                </button>
              </div>
            ),
          )
        )}
      </div>

      <form className="card form-stack" onSubmit={addDrink}>
        <h2>Add drink</h2>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          A price cannot be changed once a drink exists — historical purchases read it
          directly. To reprice, remove the drink and add a new one.
        </p>
        <div className="form-grid">
          <label>
            Description
            <input
              maxLength={200}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              required
            />
          </label>
          <label>
            Price (NZD)
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={price}
              onChange={(event) => setPrice(event.target.value)}
              required
            />
          </label>
        </div>
        <button className="btn" type="submit" disabled={busy}>
          {busy ? 'Adding…' : 'Add drink'}
        </button>
        {message ? <p className="form-message">{message}</p> : null}
      </form>
    </Shell>
  )
}

export function AdminCataloguePage() {
  return (
    <Require roles={['admin']} showDenied>
      <CatalogueInner />
    </Require>
  )
}
